/* eslint-disable react/no-unused-prop-types -- Search navigation data remains part of the page contract. */
import styled from 'styled-components';
import type { HomePageViewModel } from '@lib/page-data/home.map';
import type { Locale } from '@lib/locale';
import GreetingSection from '../GreetingSection/GreetingSection';
import RenderLayoutViews from '../shared/RenderLayoutViews';
import { isUserLoggedIn } from '../../../../lib/user';
import type { PageInjectedProps } from './Page';

type HomeContentProps = Pick<HomePageViewModel,
  'globalGreetingSectionConfig' | 'adConfig' | 'anonLayout' | 'loggedLayout'> & {
  urlLocale: Locale;
  searchNavBarData?: HomePageViewModel['searchNavBarData'];
  headerSectionData: HomePageViewModel['headerSection'];
  pageHeader: string | null | undefined;
} & Partial<PageInjectedProps>;

const ScreenReaderOnlyH1 = styled.h1`
  position:absolute;
  left:-10000px;
  top:auto;
  width:1px;
  height:1px;
  overflow:hidden;
`;

const HomeContent = (props: HomeContentProps) => {
  const {
    userData,
    globalGreetingSectionConfig,
    urlLocale,
    userShopsData,
    pageStateModifiers,
    adConfig,
    anonLayout,
    loggedLayout,
    headerSectionData,
    pageHeader,
    globalPageConfig,
  } = props;

  const {
    greetingSectionLayoutPosition = 0,
  } = globalGreetingSectionConfig || {};
  const greetingPosition = greetingSectionLayoutPosition ?? 0;

  const preGreetingSectionLayout = loggedLayout?.slice(0, greetingPosition);
  const postGreetingSectionLayout = loggedLayout?.slice(greetingPosition);

  const userFirstName = userData?.visitInfo && typeof userData.visitInfo === 'object'
    && 'firstName' in userData.visitInfo
    ? userData.visitInfo.firstName
    : '';

  return (
    <div>
      <ScreenReaderOnlyH1>{ pageHeader }</ScreenReaderOnlyH1>
      {/* Logged Layout placed before greeting section */}
      {isUserLoggedIn(userData) && (
        <RenderLayoutViews
          key="preGreetingSectionLayout"
          views={preGreetingSectionLayout}
          locale={urlLocale}
          userData={userData}
          pageStateModifiers={pageStateModifiers}
          adConfig={adConfig}
          globalPageConfig={globalPageConfig}
        />
      )}

      <GreetingSection
        globalGreetingSectionConfig={globalGreetingSectionConfig}
        locale={urlLocale}
        isUserLoggedIn={() => isUserLoggedIn(userData)}
        firstName={userFirstName}
        userShopsData={userShopsData}
        pageStateModifiers={pageStateModifiers}
        headerSectionData={headerSectionData}
      />

      {/* Anon Layout */}
      {!isUserLoggedIn(userData) && (
        <RenderLayoutViews
          views={anonLayout}
          locale={urlLocale}
          userData={userData}
          pageStateModifiers={pageStateModifiers}
          adConfig={adConfig}
          globalPageConfig={globalPageConfig}
        />
      )}

      {/* Logged Layout placed after greeting section */}
      {isUserLoggedIn(userData) && (
        <RenderLayoutViews
          key="postGreetingSectionLayout"
          views={postGreetingSectionLayout}
          locale={urlLocale}
          userData={userData}
          pageStateModifiers={pageStateModifiers}
          adConfig={adConfig}
          globalPageConfig={globalPageConfig}
        />
      )}
    </div>
  );
};

export default HomeContent;
